import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageHeader } from '@/components/layout/page-header';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useBusiness, usePermission } from '@/features/auth/auth.store';
import { useClientFlags } from '@/config/client-flags';
import {
  toBusinessInput,
  toLocationInput,
  useBusinessInfo,
  useUpdateBusinessInfo,
  type BusinessInput,
  type LocationInput,
} from './business-info.api';
import {
  FEE_KIND,
  IVU_PROVIDERS,
  useStoreOptions,
  useTaxCredentials,
  useUpdateLocation,
  useUpdateStoreOptions,
  useUpdateTaxCredentials,
  type StoreOptions,
  type TaxCredentialsInput,
} from './store-options.api';

/**
 * How this store behaves.
 *
 * The settings come from three records and go back to three endpoints, which
 * the page hides — nobody thinks "this switch is on the location and that one
 * is on the options". What it does not hide is *whether anything changed*: the
 * Save button says how many records it is about to write, and only the ones
 * that were actually touched are sent.
 */
export function StoreOptionsPage() {
  const { t } = useTranslation();
  const business = useBusiness();
  const isAdmin = usePermission('isAdmin');
  const flags = useClientFlags();

  const info = useBusinessInfo(business?.id ?? null);
  const locationId = info.data?.locations?.[0]?.id ?? null;

  const options = useStoreOptions(locationId);
  const taxes = useTaxCredentials(locationId);

  const saveOptions = useUpdateStoreOptions();
  const saveLocation = useUpdateLocation();
  const saveBusiness = useUpdateBusinessInfo();
  const saveTaxes = useUpdateTaxCredentials(locationId);

  const [draft, setDraft] = useState<{
    options: StoreOptions;
    location: LocationInput;
    business: BusinessInput;
    taxes: TaxCredentialsInput;
  } | null>(null);
  const [baseline, setBaseline] = useState('');
  const [hydrated, setHydrated] = useState<string | null>(null);

  const ready = info.data && options.data && !taxes.isPending;
  const key = ready ? `${info.data!.id}:${options.data!.id}` : null;

  if (ready && key && hydrated !== key) {
    const next = {
      options: options.data!,
      location: toLocationInput(info.data!.locations![0]),
      business: toBusinessInput(info.data!),
      taxes: {
        merchantId: taxes.data?.merchantId ?? null,
        terminalId: taxes.data?.terminalId ?? null,
        terminalPassword: taxes.data?.terminalPassword ?? null,
        provider: taxes.data?.provider ?? 0,
      },
    };
    setHydrated(key);
    setDraft(next);
    setBaseline(JSON.stringify(next));
  }

  if (!draft) {
    return (
      <div className="space-y-6">
        <PageHeader
          title={t('storeOptions.title')}
          description={business?.name ?? ''}
        />
        {/* Options load per location, and the location comes from the store:
            without either the queries never run and a skeleton would stay up. */}
        {!business || (info.isSuccess && !locationId) ? (
          <p className="text-muted-foreground rounded-xl border border-dashed py-12 text-center text-sm">
            {t(business ? 'storeOptions.noLocation' : 'common.noStoreSelected')}
          </p>
        ) : info.isError || options.isError ? (
          <p className="text-muted-foreground rounded-xl border border-dashed py-12 text-center text-sm">
            {t('storeOptions.loadFailed')}
          </p>
        ) : (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={index} className="h-12 rounded-xl" />
            ))}
          </div>
        )}
      </div>
    );
  }

  const original = JSON.parse(baseline) as typeof draft;
  const changed = {
    options: JSON.stringify(draft.options) !== JSON.stringify(original.options),
    location:
      JSON.stringify(draft.location) !== JSON.stringify(original.location),
    business:
      JSON.stringify(draft.business) !== JSON.stringify(original.business),
    taxes: JSON.stringify(draft.taxes) !== JSON.stringify(original.taxes),
  };
  const dirtyCount = Object.values(changed).filter(Boolean).length;

  const setOptions = (patch: Partial<StoreOptions>) =>
    setDraft((current) =>
      current ? { ...current, options: { ...current.options, ...patch } } : current
    );
  const setLocation = (patch: Partial<LocationInput>) =>
    setDraft((current) =>
      current
        ? { ...current, location: { ...current.location, ...patch } }
        : current
    );
  const setBusiness = (patch: Partial<BusinessInput>) =>
    setDraft((current) =>
      current
        ? { ...current, business: { ...current.business, ...patch } }
        : current
    );
  const setTaxes = (patch: Partial<TaxCredentialsInput>) =>
    setDraft((current) =>
      current ? { ...current, taxes: { ...current.taxes, ...patch } } : current
    );

  const saving =
    saveOptions.isPending
    || saveLocation.isPending
    || saveBusiness.isPending
    || saveTaxes.isPending;

  async function save() {
    try {
      // Sequential rather than parallel: three writes against one store, and
      // a half-applied save is easier to reason about in order.
      if (changed.options) await saveOptions.mutateAsync(draft!.options);
      if (changed.location) await saveLocation.mutateAsync(draft!.location);
      if (changed.business) await saveBusiness.mutateAsync(draft!.business);
      if (changed.taxes) await saveTaxes.mutateAsync(draft!.taxes);
      setBaseline(JSON.stringify(draft));
      toast.success(t('storeOptions.saved'));
    } catch {
      toast.error(t('storeOptions.actionFailed'));
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('storeOptions.title')}
        description={business?.name ?? t('storeOptions.subtitle')}
        actions={
          <Button size="sm" disabled={saving || dirtyCount === 0} onClick={save}>
            {saving && <Loader2 className="animate-spin" />}
            {t('common.save')}
            {dirtyCount > 0 && (
              <Badge variant="secondary">{dirtyCount}</Badge>
            )}
          </Button>
        }
      />

      <Tabs defaultValue="storefront">
        <TabsList className="flex-wrap">
          <TabsTrigger value="storefront">
            {t('storeOptions.storefront')}
          </TabsTrigger>
          <TabsTrigger value="notifications">
            {t('storeOptions.notifications')}
          </TabsTrigger>
          <TabsTrigger value="orders">{t('storeOptions.orders')}</TabsTrigger>
          <TabsTrigger value="products">
            {t('storeOptions.products')}
          </TabsTrigger>
          {flags.hasLayaway && (
            <TabsTrigger value="layaway">
              {t('storeOptions.layaway')}
            </TabsTrigger>
          )}
          <TabsTrigger value="giftcards">
            {t('storeOptions.giftCards')}
          </TabsTrigger>
          <TabsTrigger value="tax">{t('storeOptions.tax')}</TabsTrigger>
        </TabsList>

        {/* --------------------------------------------------- storefront */}
        <TabsContent value="storefront" className="space-y-4">
          <Section title={t('storeOptions.departments')}>
            <Toggle
              label={t('storeOptions.sponsoredDepartment')}
              checked={draft.location.showSponsoredDepartment}
              onChange={(showSponsoredDepartment) =>
                setLocation({ showSponsoredDepartment })
              }
            />
            <Toggle
              label={t('storeOptions.offersDepartment')}
              checked={draft.location.showOffersDepartment}
              onChange={(showOffersDepartment) =>
                setLocation({ showOffersDepartment })
              }
            />
            <Toggle
              label={t('storeOptions.recentlyAddedDepartment')}
              checked={draft.location.showRecentlyAddedDepartment}
              onChange={(showRecentlyAddedDepartment) =>
                setLocation({ showRecentlyAddedDepartment })
              }
            />
            <Toggle
              label={t('storeOptions.healthyDepartment')}
              checked={draft.location.showHealthyDepartment}
              onChange={(showHealthyDepartment) =>
                setLocation({ showHealthyDepartment })
              }
            />
            <Toggle
              label={t('storeOptions.madeLocallyDepartment')}
              checked={draft.location.showMadeLocallyDepartment}
              onChange={(showMadeLocallyDepartment) =>
                setLocation({ showMadeLocallyDepartment })
              }
            />
          </Section>

          <Section title={t('storeOptions.searchFilters')}>
            <Toggle
              label={t('storeOptions.defaultFilters')}
              checked={draft.options.showDefaultSearchFilters}
              onChange={(showDefaultSearchFilters) =>
                setOptions({ showDefaultSearchFilters })
              }
            />
            <Toggle
              label={t('storeOptions.healthyFilters')}
              checked={draft.options.showHealthySearchFilters}
              onChange={(showHealthySearchFilters) =>
                setOptions({ showHealthySearchFilters })
              }
            />
            <Toggle
              label={t('storeOptions.brandFilters')}
              checked={draft.options.showBrandSearchFilters}
              onChange={(showBrandSearchFilters) =>
                setOptions({ showBrandSearchFilters })
              }
            />
            <Toggle
              label={t('storeOptions.customFilters')}
              checked={draft.options.showCustomSearchFilters}
              onChange={(showCustomSearchFilters) =>
                setOptions({ showCustomSearchFilters })
              }
            />
          </Section>

          {/* The store's own PayPal application, not the platform's. */}
          <Section title={t('storeOptions.paypal')}>
            <p className="text-muted-foreground text-xs">
              {t('storeOptions.paypalHint')}
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('storeOptions.paypalClientId')}>
                <Secret
                  value={draft.location.paypalClientId ?? ''}
                  onChange={(paypalClientId) =>
                    setLocation({ paypalClientId })
                  }
                />
              </Field>
              <Field label={t('storeOptions.paypalClientSecret')}>
                <Secret
                  value={draft.location.paypalClientSecret ?? ''}
                  onChange={(paypalClientSecret) =>
                    setLocation({ paypalClientSecret })
                  }
                />
              </Field>
            </div>
          </Section>
        </TabsContent>

        {/* ------------------------------------------------ notifications */}
        <TabsContent value="notifications" className="space-y-4">
          <Section title={t('storeOptions.whoIsTold')}>
            <Toggle
              label={t('storeOptions.notifyStore')}
              hint={t('storeOptions.notifyStoreHint')}
              checked={draft.location.sendEmailNotifications}
              onChange={(sendEmailNotifications) =>
                setLocation({ sendEmailNotifications })
              }
            />
            <Toggle
              label={t('storeOptions.delayInOrders')}
              checked={draft.options.sendDelayInOrdersAlert}
              onChange={(sendDelayInOrdersAlert) =>
                setOptions({ sendDelayInOrdersAlert })
              }
            />
            <Toggle
              label={t('storeOptions.pickingFinished')}
              checked={draft.options.sendPickingFinishedAlert}
              onChange={(sendPickingFinishedAlert) =>
                setOptions({ sendPickingFinishedAlert })
              }
            />
            <Toggle
              label={t('storeOptions.packingFinished')}
              checked={draft.options.sendPackingFinishedAlert}
              onChange={(sendPackingFinishedAlert) =>
                setOptions({ sendPackingFinishedAlert })
              }
            />
          </Section>
        </TabsContent>

        {/* ------------------------------------------------------- orders */}
        <TabsContent value="orders" className="space-y-4">
          <Section title={t('storeOptions.takingOrders')}>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label={t('storeOptions.codePrefix')}
                hint={t('storeOptions.codePrefixHint')}
              >
                <Input
                  value={draft.options.confirmationCodePrefix ?? ''}
                  onChange={(event) =>
                    setOptions({ confirmationCodePrefix: event.target.value })
                  }
                />
              </Field>
              <Field label={t('storeOptions.minimumPickup')}>
                <Money
                  value={draft.business.minimumPickupOrderAmount}
                  onChange={(minimumPickupOrderAmount) =>
                    setBusiness({ minimumPickupOrderAmount })
                  }
                />
              </Field>
              <Field label={t('storeOptions.minimumDelivery')}>
                <Money
                  value={draft.business.minimumDeliveryOrderAmount}
                  onChange={(minimumDeliveryOrderAmount) =>
                    setBusiness({ minimumDeliveryOrderAmount })
                  }
                />
              </Field>
              <Field
                label={t('storeOptions.additionalItemLimit')}
                hint={t('storeOptions.additionalItemLimitHint')}
              >
                <Money
                  value={draft.location.additionalOrderItemsLimit}
                  onChange={(additionalOrderItemsLimit) =>
                    setLocation({ additionalOrderItemsLimit })
                  }
                />
              </Field>
            </div>

            <Toggle
              label={t('storeOptions.allowAdditionalOrders')}
              checked={draft.options.allowAdditionalOrders}
              onChange={(allowAdditionalOrders) =>
                setOptions({ allowAdditionalOrders })
              }
            />
            <Toggle
              label={t('storeOptions.allowOrderModification')}
              checked={draft.options.allowOrderModification}
              onChange={(allowOrderModification) =>
                setOptions({ allowOrderModification })
              }
            />
            <Toggle
              label={t('storeOptions.authAtOrderEntry')}
              hint={t('storeOptions.authAtOrderEntryHint')}
              checked={draft.options.authAtOrderEntry}
              onChange={(authAtOrderEntry) => setOptions({ authAtOrderEntry })}
            />
            <Toggle
              label={t('storeOptions.managedBins')}
              checked={draft.options.useManagedStoreBins}
              onChange={(useManagedStoreBins) =>
                setOptions({ useManagedStoreBins })
              }
            />
          </Section>

          <Section title={t('storeOptions.cancellation')}>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('storeOptions.cancellationFee')}>
                <Money
                  value={draft.options.cancellationFee}
                  onChange={(cancellationFee) =>
                    setOptions({ cancellationFee })
                  }
                />
              </Field>
              <Field label={t('storeOptions.feeType')}>
                <FeeKind
                  value={draft.options.cancellationFeeType}
                  onChange={(cancellationFeeType) =>
                    setOptions({ cancellationFeeType })
                  }
                />
              </Field>
            </div>
          </Section>

          <Section title={t('storeOptions.pickingAndPacking')}>
            <Toggle
              label={t('storeOptions.markPickedAsPacked')}
              checked={draft.options.markPickedAsPacked}
              onChange={(markPickedAsPacked) =>
                setOptions({ markPickedAsPacked })
              }
            />
            <Toggle
              label={t('storeOptions.markNotPackedAsOut')}
              checked={draft.options.markNotPackedAsOut}
              onChange={(markNotPackedAsOut) =>
                setOptions({ markNotPackedAsOut })
              }
            />
          </Section>

          <Section title={t('storeOptions.payouts')}>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label={t('storeOptions.stripeDelay')}
                hint={t('storeOptions.transferDelayHint')}
              >
                <Money
                  value={draft.options.stripeTransferDelay}
                  onChange={(stripeTransferDelay) =>
                    setOptions({ stripeTransferDelay })
                  }
                />
              </Field>
              <Field
                label={t('storeOptions.paypalDelay')}
                hint={t('storeOptions.transferDelayHint')}
              >
                <Money
                  value={draft.options.payPalTransferDelay}
                  onChange={(payPalTransferDelay) =>
                    setOptions({ payPalTransferDelay })
                  }
                />
              </Field>
            </div>
          </Section>
        </TabsContent>

        {/* ----------------------------------------------------- products */}
        <TabsContent value="products" className="space-y-4">
          <Section title={t('storeOptions.outOfStock')}>
            <Toggle
              label={t('storeOptions.oosTrigger')}
              hint={t('storeOptions.oosTriggerHint')}
              checked={draft.options.outOfStockTrigger}
              onChange={(outOfStockTrigger) =>
                setOptions({ outOfStockTrigger })
              }
            />
            <Field
              label={t('storeOptions.oosLimit')}
              hint={t('storeOptions.oosLimitHint')}
            >
              <Money
                value={draft.options.outOfStockCountRequirement}
                onChange={(outOfStockCountRequirement) =>
                  setOptions({ outOfStockCountRequirement })
                }
              />
            </Field>
          </Section>

          <Section title={t('storeOptions.whatShoppersSee')}>
            <Toggle
              label={t('storeOptions.subsByDefault')}
              hint={t('storeOptions.subsByDefaultHint')}
              checked={draft.options.subsSelectedByDefault}
              onChange={(subsSelectedByDefault) =>
                setOptions({ subsSelectedByDefault })
              }
            />
            <Toggle
              label={t('storeOptions.hideNoPictures')}
              checked={draft.options.hideProductsWithNoPictures}
              onChange={(hideProductsWithNoPictures) =>
                setOptions({ hideProductsWithNoPictures })
              }
            />
          </Section>
        </TabsContent>

        {/* ------------------------------------------------------ layaway */}
        {flags.hasLayaway && (
          <TabsContent value="layaway" className="space-y-4">
            <Section title={t('storeOptions.layaway')}>
              <Toggle
                label={t('storeOptions.hasLayaway')}
                hint={t('storeOptions.hasLayawayHint')}
                checked={draft.business.hasLayaway}
                onChange={(hasLayaway) => setBusiness({ hasLayaway })}
              />

              <div className="grid gap-3 sm:grid-cols-2">
                <Field
                  label={t('storeOptions.layawayExpirationDays')}
                  hint={t('storeOptions.layawayExpirationDaysHint')}
                >
                  <Money
                    value={draft.business.layawayExpirationDays}
                    onChange={(layawayExpirationDays) =>
                      setBusiness({ layawayExpirationDays })
                    }
                  />
                </Field>
                <Field
                  label={t('storeOptions.maxLayaways')}
                  hint={t('storeOptions.maxLayawaysHint')}
                >
                  <Money
                    value={draft.business.maxLayawaysPerCustomer}
                    onChange={(maxLayawaysPerCustomer) =>
                      setBusiness({ maxLayawaysPerCustomer })
                    }
                  />
                </Field>
              </div>
            </Section>

            {/* Four amounts, each with its own none/fixed/percentage. */}
            <Section title={t('storeOptions.layawayAmounts')}>
              <AmountAndKind
                label={t('storeOptions.layawayServiceFee')}
                amount={draft.business.layawayServiceFee}
                kind={draft.business.layawayServiceFeeType}
                onAmount={(layawayServiceFee) =>
                  setBusiness({ layawayServiceFee })
                }
                onKind={(layawayServiceFeeType) =>
                  setBusiness({ layawayServiceFeeType })
                }
              />
              <AmountAndKind
                label={t('storeOptions.layawayInitialPayment')}
                amount={draft.business.layawayInitialPayment}
                kind={draft.business.layawayInitialPaymentType}
                onAmount={(layawayInitialPayment) =>
                  setBusiness({ layawayInitialPayment })
                }
                onKind={(layawayInitialPaymentType) =>
                  setBusiness({ layawayInitialPaymentType })
                }
              />
              <AmountAndKind
                label={t('storeOptions.layawayPaymentMinimum')}
                amount={draft.business.layawayPaymentMinimum}
                kind={draft.business.layawayPaymentMinimumType}
                onAmount={(layawayPaymentMinimum) =>
                  setBusiness({ layawayPaymentMinimum })
                }
                onKind={(layawayPaymentMinimumType) =>
                  setBusiness({ layawayPaymentMinimumType })
                }
              />
              <AmountAndKind
                label={t('storeOptions.layawayPenalty')}
                amount={draft.business.layawayDefaultPenalty}
                kind={draft.business.layawayDefaultPenaltyType}
                onAmount={(layawayDefaultPenalty) =>
                  setBusiness({ layawayDefaultPenalty })
                }
                onKind={(layawayDefaultPenaltyType) =>
                  setBusiness({ layawayDefaultPenaltyType })
                }
              />
            </Section>
          </TabsContent>
        )}

        {/* ---------------------------------------------------- giftcards */}
        <TabsContent value="giftcards" className="space-y-4">
          <Section title={t('storeOptions.activationFee')}>
            <p className="text-muted-foreground text-xs">
              {t('storeOptions.activationFeeHint')}
            </p>
            <Money
              value={draft.options.giftCardActivationFee ?? 0}
              onChange={(giftCardActivationFee) =>
                setOptions({ giftCardActivationFee })
              }
            />
          </Section>
        </TabsContent>

        {/* ---------------------------------------------------------- tax */}
        <TabsContent value="tax" className="space-y-4">
          <Section title={t('storeOptions.tax')}>
            <p className="text-muted-foreground text-xs">
              {t('storeOptions.taxHint')}
            </p>

            <Field label={t('storeOptions.taxProvider')}>
              <Select
                value={String(draft.taxes.provider)}
                disabled={!isAdmin}
                onValueChange={(value) =>
                  setTaxes({ provider: Number(value) })
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {IVU_PROVIDERS.map((provider) => (
                    <SelectItem key={provider} value={String(provider)}>
                      {t(`storeOptions.taxProvider_${provider}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <div className="grid gap-3 sm:grid-cols-3">
              <Field label={t('storeOptions.merchantId')}>
                <Input
                  value={draft.taxes.merchantId ?? ''}
                  disabled={!isAdmin}
                  onChange={(event) =>
                    setTaxes({ merchantId: event.target.value })
                  }
                />
              </Field>
              <Field label={t('storeOptions.terminalId')}>
                <Input
                  value={draft.taxes.terminalId ?? ''}
                  disabled={!isAdmin}
                  onChange={(event) =>
                    setTaxes({ terminalId: event.target.value })
                  }
                />
              </Field>
              <Field label={t('storeOptions.terminalPassword')}>
                <Secret
                  value={draft.taxes.terminalPassword ?? ''}
                  disabled={!isAdmin}
                  onChange={(terminalPassword) => setTaxes({ terminalPassword })}
                />
              </Field>
            </div>
          </Section>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3 rounded-xl border p-4">
      <h2 className="text-sm font-medium">{title}</h2>
      {children}
    </section>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
    </div>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
      <span className="min-w-0">
        <span className="block text-sm">{label}</span>
        {hint && (
          <span className="text-muted-foreground block text-xs">{hint}</span>
        )}
      </span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </label>
  );
}

/** An amount and the none/fixed/percentage that reads it. */
function AmountAndKind({
  label,
  amount,
  kind,
  onAmount,
  onKind,
}: {
  label: string;
  amount: number;
  kind: number;
  onAmount: (value: number) => void;
  onKind: (value: number) => void;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Field label={label}>
        <Money value={amount} onChange={onAmount} />
      </Field>
      <Field label=" ">
        <FeeKind value={kind} onChange={onKind} />
      </Field>
    </div>
  );
}

/**
 * A credential: masked until asked for.
 *
 * Hidden by default because these sit on a settings page somebody may well be
 * sharing a screen on, and revealable because they have to be checked against
 * what the provider issued.
 */
function Secret({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [shown, setShown] = useState(false);

  return (
    <div className="relative">
      <Input
        type={shown ? 'text' : 'password'}
        className="pr-10"
        autoComplete="off"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
      <button
        type="button"
        aria-label={shown ? t('storeOptions.hide') : t('storeOptions.reveal')}
        className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2 -translate-y-1/2"
        onClick={() => setShown((current) => !current)}
      >
        {shown ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
      </button>
    </div>
  );
}

function Money({
  value,
  onChange,
}: {
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <Input
      type="number"
      inputMode="decimal"
      min={0}
      step="0.01"
      value={value}
      onChange={(event) => onChange(Number(event.target.value))}
    />
  );
}

function FeeKind({
  value,
  onChange,
}: {
  value: number;
  onChange: (value: number) => void;
}) {
  const { t } = useTranslation();

  return (
    <Select value={String(value)} onValueChange={(v) => onChange(Number(v))}>
      <SelectTrigger className="w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={String(FEE_KIND.none)}>
          {t('storeOptions.feeNone')}
        </SelectItem>
        <SelectItem value={String(FEE_KIND.fixed)}>
          {t('storeOptions.feeFixed')}
        </SelectItem>
        <SelectItem value={String(FEE_KIND.percentage)}>
          {t('storeOptions.feePercentage')}
        </SelectItem>
      </SelectContent>
    </Select>
  );
}
